import { getCurrentBadgeLevel, getCurrentStepIndex } from '@utils/badgeUtils'
import { appSessionStorage } from '@utils/storageUtils'

import { AvatarMetaKeys } from '@suntex.fim/enums'

import {
  STICKER_REQUIREMENTS,
  getAllAvailableAvatars,
  skillAvatars,
  specialAvatars
} from '../constants/avatarConstants'
import type { AvatarItem, AvatarMetaData } from '../types/avatar'

import { isValidNumber, isValidObject, isValidString } from './utilities'

/**
 * Calculate the current player level based on sticker count
 */
export const calculateCurrentLevel = (stickerCount?: number): number => {
  if (!stickerCount) return 0

  const currentBadgeLevel = getCurrentBadgeLevel(stickerCount)
  if (!currentBadgeLevel) return 0

  const badgeThreshold = Number(currentBadgeLevel.description.replace(/,/g, ''))
  let extraLevel = 0
  if (stickerCount > badgeThreshold) {
    extraLevel = Math.floor(stickerCount / badgeThreshold) - 1
  }

  return getCurrentStepIndex(currentBadgeLevel) + extraLevel
}

/**
 * Find avatar by name from all available avatars
 */
export const findAvatarByName = (name: string): AvatarItem | null => {
  const allAvatars = getAllAvailableAvatars()
  return allAvatars.find((avatar) => avatar.name === name) ?? null
}

/**
 * Get default avatar based on current level and sticker count
 * Returns the latest unlocked icon - prioritizes special sticker avatars if unlocked,
 * otherwise returns the latest skill level icon
 */
export const getDefaultAvatar = (
  currentLevel: number,
  currentStickers?: number
): AvatarItem => {
  const stickerCount = currentStickers ?? getCurrentStickerCount()

  // Check for latest unlocked special sticker avatar (highest priority)
  if (stickerCount >= STICKER_REQUIREMENTS.SPECIAL_AVATARS) {
    // Find the latest special avatar that is unlocked (reverse iteration without for loop)
    const latestSpecialAvatar = specialAvatars
      .map((avatar, index) => ({ avatar, index }))
      .reverse()
      .find(({ index }) =>
        isSpecialAvatarUnlocked(index, currentLevel, stickerCount)
      )

    if (latestSpecialAvatar) {
      return latestSpecialAvatar.avatar
    }
  }

  // Fall back to latest unlocked skill level icon
  const maxSkillIndex = Math.min(currentLevel, skillAvatars.length - 1)
  const skillIndex = Math.max(0, maxSkillIndex) // Ensure at least index 0

  return skillAvatars[skillIndex] ?? skillAvatars[0]
}

/**
 * Check if avatar is unlocked based on sticker count and level
 */
export const isAvatarUnlocked = (
  stickerRequirement: number,
  currentStickers: number,
  avatarIndex?: number,
  currentLevel?: number
): boolean => {
  if (isValidNumber(avatarIndex) && isValidNumber(currentLevel)) {
    return avatarIndex <= currentLevel
  }
  return currentStickers >= stickerRequirement
}

/**
 * Check if special sticker avatar is unlocked based on level and sticker count
 * Special avatars require: level + offset AND high sticker count (10,000+)
 */
export const isSpecialAvatarUnlocked = (
  avatarIndex: number,
  currentLevel: number,
  currentStickers: number
): boolean => {
  // Must have high level (skill level + offset) AND high sticker count
  return (
    avatarIndex + STICKER_REQUIREMENTS.SPECIAL_AVATARS_OFFSET <= currentLevel &&
    currentStickers >= STICKER_REQUIREMENTS.SPECIAL_AVATARS
  )
}

/**
 * Validate avatar metadata
 */
export const validateAvatarData = (data: unknown): data is AvatarMetaData => {
  return (
    isValidObject(data) &&
    AvatarMetaKeys.BgColor in data &&
    AvatarMetaKeys.AvatarIcon in data &&
    isValidString(data.BgColor) &&
    isValidString(data.AvatarIcon)
  )
}

/**
 * Get current user sticker count safely
 */
export const getCurrentStickerCount = (): number => {
  return appSessionStorage.stickerCount ?? 0
}
